import React, {
  ChangeEvent,
  FC,
  KeyboardEvent,
  MutableRefObject,
  ReactElement,
  RefObject,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Scrollbars } from 'react-custom-scrollbars';

import { useToggleDropdown } from 'src/front/common/hooks/temporary';
import styled from 'styled-components';

const KEY_CODES = {
  ENTER: 13,
  ESCAPE: 27,
  UP: 38,
  DOWN: 40,
};

const padding = {
  inputLabelOnTop: '14px',
  inputLabelOnBottom: '25px',
};

const SCROLLBAR_HEIGHT = 230;
const THUMB_SIZE = 124;
const SCROLL_TOP_MARGIN = 10;
const SCROLL_BOTTOM_MARGIN = 10;

const Wrapper = styled.div`
  position: relative;

  width: auto;
  height: auto;
`;

export enum InputType {
  Password = 'password',
  Text = 'text',
}

const getInputBorder = ({ theme, error }: any): string => {
  if (error) {
    return `1px solid ${theme.palette.butterscotch}`;
  }

  return `1px solid ${theme.palette.greenyGrey}`;
};

const InputWrapper = styled.div`
  position: relative;

  width: auto;
  height: auto;
`;

const getInputLabelColor = ({ theme, error, labelOnTop }: any): string => {
  if (error) {
    return theme.palette.pastelOrange;
  }
  if (labelOnTop) {
    return theme.palette.blueyGrey;
  }
  return theme.palette.blueGrey;
};

const InputLabel = styled.label<{ labelOnTop: boolean; error: boolean }>`
  position: absolute;
  z-index: 1;
  top: ${({ theme }) => padding.inputLabelOnBottom};
  left: 20px;

  user-select: none;
  pointer-events: none;

  color: ${(props) => getInputLabelColor(props)};

  ${({ labelOnTop, theme }) =>
    labelOnTop
      ? `
    top: ${padding.inputLabelOnTop};
    font-size: 12px;
    font-family: ${theme.fonts.GothamLight};
    font-weight: 400;
    height: 11px;`
      : `
    font-family: ${theme.fonts.GothamMedium};
    font-weight: 500;
    `}
`;

const StyledInput = styled.input<{ error: boolean }>`
  box-sizing: border-box;
  width: 100%;
  height: 60px;
  padding: 33px 20px 15px;
  border: ${(props) => getInputBorder(props)};
  border-radius: 3px;

  color: ${({ theme }) => theme.palette.black};

  ${({ theme }) => `font-family: ${theme.fonts.GothamMedium};`}
  font-size: 14px;
  font-weight: bold;
  outline: none;

  &:disabled {
    opacity: 0.5;

    background-color: ${({ theme }) => theme.palette.icicle30};
  }

  &:focus {
    + ${InputLabel} {
      top: ${padding.inputLabelOnTop};

      height: 11px;

      color: ${({ theme }) => theme.palette.blueyGrey};

      font-size: 12px;
      ${({ theme }) => `font-family: ${theme.fonts.GothamLight};`}
      font-weight: 400;
    }
  }
`;

const Listbox = styled.ul`
  position: absolute;
  z-index: 10;

  width: 100%;
  padding: 0;
  border: solid 1px ${({ theme }) => theme.palette.whiteThree};
  border-radius: 2px;
  box-shadow: 0 12px 24px 0 rgba(35, 43, 56, 0.15);

  margin-top: 10px;

  list-style: none;

  background-color: ${({ theme }) => theme.palette.white};
  color: ${({ theme }) => theme.palette.blueGrey};

  ${({ theme }) => `font-family: ${theme.fonts.GothamMedium};`}
  font-size: 13px;
`;

const StyledOption = styled.li<{ focused: boolean }>`
  display: flex;
  padding: 10px 0;

  &::before {
    display: block;
    width: 5px;
    height: 5px;
    content: '';

    border: 2px solid ${({ theme }) => theme.palette.white};
    border-radius: 50%;
    align-self: center;

    margin: 0 10px;

    background-color: ${({ theme }) => theme.palette.lightBlueGrey};
  }

  ${({ focused, theme }) =>
    focused
      ? `
      color: ${theme.palette.black};
      cursor: pointer;
      &::before {
        box-shadow: 0px 0px 0px 1px ${theme.palette.deepSeaBlue};

        background-color: ${theme.palette.deepSeaBlue};
      }
    `
      : ''}

  &:hover {
    color: ${({ theme }) => theme.palette.black};
    cursor: pointer;
    &::before {
      box-shadow: 0px 0px 0px 1px ${({ theme }) => theme.palette.deepSeaBlue};

      background-color: ${({ theme }) => theme.palette.deepSeaBlue};
    }
  }

  &:first-of-type {
    margin-top: ${SCROLL_TOP_MARGIN}px;
  }

  &:last-of-type {
    margin-bottom: ${SCROLL_BOTTOM_MARGIN}px;
  }
`;

const InputError = styled.p`
  height: 19px;
  margin-left: 5px;
  margin-top: 8px;

  color: ${({ theme }) => theme.palette.butterscotch};

  ${({ theme }) => `font-family: ${theme.fonts.GothamLight};`}
  font-size: 14px;
  font-weight: 400;
`;

const getHighligtedText = (query: string, text: string): ReactElement => {
  const parts = text.split(query);
  return (
    <span>
      {parts.map((part, idx) =>
        idx === parts.length - 1 ? (
          part
        ) : (
          <>
            {part}
            <b>{query}</b>{' '}
          </>
        )
      )}
    </span>
  );
};

interface Props {
  disabled?: boolean;
  errorMessage?: string;
  label: string;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  value: string;
  className?: string;
  name: string;
  required?: boolean;
  items: any[];
  onItemSelect?: (item: any) => void;
  dropdownHeight?: number;
}

const SuggestionDropdown: FC<Props> = ({
  disabled = false,
  errorMessage,
  label,
  onChange,
  value,
  className = '',
  name,
  required = false,
  items,
  onItemSelect,
  dropdownHeight,
}) => {
  const [selected, setSelected] = useState<number>(-1);
  const inputRef = useRef<HTMLElement>();
  const scrollbarRef = useRef<Scrollbars>(null);
  const { toggleDropdown, setToggleDropdown, refDropdown } = useToggleDropdown(inputRef);

  const selectItem = (item: any) => (): void => {
    onItemSelect && onItemSelect(item);
    setToggleDropdown(false);
    inputRef.current?.blur();
  };

  const handleOptionFocus = (optionIndex: number): void => setSelected(optionIndex);

  useEffect(() => {
    if (!toggleDropdown) {
      setSelected(-1);
    }
  }, [toggleDropdown]);

  const handleScrollUp = (itemIndex: number): void => {
    const position = scrollbarRef.current?.getValues();

    if (position) {
      const { clientHeight, scrollHeight, scrollTop } = position;
      const singleItemHeight =
        (scrollHeight - SCROLL_BOTTOM_MARGIN - SCROLL_TOP_MARGIN) / items.length;

      if (clientHeight > singleItemHeight * (items.length - itemIndex)) return;

      scrollbarRef.current?.scrollTop(scrollTop - singleItemHeight);
    }
  };

  const handleScrollDown = (itemIndex: number): void => {
    const position = scrollbarRef.current?.getValues();

    if (position) {
      const { clientHeight, scrollHeight, scrollTop } = position;
      const singleItemHeight =
        (scrollHeight - SCROLL_BOTTOM_MARGIN - SCROLL_TOP_MARGIN) / items.length;

      if (clientHeight > singleItemHeight * (itemIndex + 1)) return;

      scrollbarRef.current?.scrollTop(scrollTop + singleItemHeight);
    }
  };

  const handleUpArrow = (event: KeyboardEvent): void => {
    const isNotAtTop = selected !== -1;
    const allowMoveUp = isNotAtTop && toggleDropdown;
    if (allowMoveUp) {
      handleScrollUp(selected - 1);
      handleOptionFocus(selected - 1);
    }
  };
  const handleDownArrow = (event: KeyboardEvent): void => {
    const isNotAtBottom = selected !== items.length - 1;
    const allowMoveDown = isNotAtBottom && toggleDropdown;
    if (allowMoveDown) {
      handleScrollDown(selected + 1);
      handleOptionFocus(selected + 1);
    }
  };
  const handleEnter = (event: KeyboardEvent): void => {
    if (!toggleDropdown) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    if (selected !== -1) {
      setSelected(-1);
      selectItem(items[selected])();
    }
  };

  const handleEscape = (event: KeyboardEvent): void => {
    setToggleDropdown(false);
    inputRef.current?.blur();
  };

  const handleKeyDown = (event: KeyboardEvent): void => {
    event.persist();
    switch (event.keyCode) {
      case KEY_CODES.UP:
        handleUpArrow(event);
        break;
      case KEY_CODES.DOWN:
        handleDownArrow(event);
        break;
      case KEY_CODES.ENTER:
        handleEnter(event);
        break;
      case KEY_CODES.ESCAPE:
        handleEscape(event);
        break;
      default:
        break;
    }
  };

  const handleOnFocus = (): void => {
    inputRef.current?.focus();
    setToggleDropdown(true);
  };

  return (
    <div className={className}>
      <Wrapper onKeyDown={handleKeyDown} tabIndex={0}>
        <InputWrapper>
          <StyledInput
            disabled={disabled}
            error={Boolean(errorMessage)}
            name={name}
            onChange={onChange}
            onFocus={handleOnFocus}
            ref={inputRef as MutableRefObject<HTMLInputElement>}
            type="text"
            value={value}
          />
          <InputLabel error={Boolean(errorMessage)} labelOnTop={Boolean(value)}>
            {`${label}${required ? ' *' : ''}`}
          </InputLabel>
        </InputWrapper>
        {errorMessage ? <InputError>{errorMessage}</InputError> : null}

        {toggleDropdown && !!items?.length ? (
          <Listbox
            aria-labelledby="combo_desc"
            ref={refDropdown as RefObject<HTMLUListElement>}
            role="listbox"
            tabIndex={-1}
          >
            <Scrollbars
              autoHeightMax={dropdownHeight || SCROLLBAR_HEIGHT}
              ref={scrollbarRef}
              thumbSize={THUMB_SIZE}
              autoHeight
              autoHide
            >
              {items?.map((result, index) => (
                <StyledOption
                  key={result.value}
                  focused={selected === index}
                  onClick={selectItem(result)}
                  role="option"
                >
                  {getHighligtedText(value || '', result.value)}
                </StyledOption>
              ))}
            </Scrollbars>
          </Listbox>
        ) : null}
      </Wrapper>
    </div>
  );
};

export default SuggestionDropdown;
